import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, TrendingUp, AlertCircle, CheckCircle, Clock, Zap } from 'lucide-react';

const HealthDashboard = () => {
  const [healthScore, setHealthScore] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHealthData();
  }, []);

  const fetchHealthData = async () => {
    try {
      // Fetch health score
      const healthResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/c8/health/score`, {
        credentials: 'include'
      });
      const healthResult = await healthResponse.json();
      if (healthResult.success) {
        setHealthScore(healthResult.data);
      }

      // Fetch today's insights
      const insightsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/c8/insights/today`, {
        credentials: 'include'
      });
      const insightsResult = await insightsResponse.json();
      if (insightsResult.success) {
        setInsights(insightsResult.insights || []);
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'from-green-500/20 to-emerald-500/20 border-green-500/30';
      case 'good': return 'from-cyan-500/20 to-blue-500/20 border-cyan-500/30';
      case 'fair': return 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
      case 'poor': return 'from-orange-500/20 to-red-500/20 border-orange-500/30';
      case 'critical': return 'from-red-500/20 to-pink-500/20 border-red-500/30';
      default: return 'from-slate-500/20 to-slate-600/20 border-slate-500/30';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'performance': return <Zap className="text-cyan-400" size={20} />;
      case 'engagement': return <Activity className="text-blue-400" size={20} />;
      case 'growth': return <TrendingUp className="text-green-400" size={20} />;
      case 'warning': return <AlertCircle className="text-orange-400" size={20} />;
      case 'opportunity': return <CheckCircle className="text-purple-400" size={20} />;
      default: return <Clock className="text-slate-400" size={20} />;
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-500/20 text-red-400 border-red-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return colors[priority] || colors.low;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="border-b border-white/5 backdrop-blur-xl bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Activity className="inline mr-3 text-cyan-400" size={36} />
                AI Health Score
              </h1>
              <p className="text-slate-400 text-lg">Santé globale de votre activité en temps réel</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Health Score Card */}
          {healthScore && (
            <div className="col-span-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`bg-gradient-to-br ${getStatusColor(healthScore.status)} backdrop-blur-xl rounded-2xl border p-8`}
              >
                <h2 className="text-white text-xl font-semibold mb-6">Score Global</h2>
                
                {/* Score Circle */}
                <div className="flex items-center justify-center mb-6">
                  <div className="relative w-48 h-48">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="96"
                        cy="96"
                        r="88"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth="12"
                        fill="none"
                      />
                      <motion.circle
                        cx="96"
                        cy="96"
                        r="88"
                        stroke="url(#gradient)"
                        strokeWidth="12"
                        fill="none"
                        strokeLinecap="round"
                        initial={{ strokeDasharray: 553, strokeDashoffset: 553 }}
                        animate={{ strokeDashoffset: 553 - (553 * healthScore.overall_score) / 100 }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#06b6d4" />
                          <stop offset="100%" stopColor="#3b82f6" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-5xl font-bold text-white">{healthScore.overall_score}</div>
                      <div className="text-slate-400 text-sm mt-1">{healthScore.status}</div>
                    </div>
                  </div>
                </div>

                {/* Dimensions */}
                <div className="space-y-3">
                  {healthScore.dimensions.map((dim, index) => (
                    <motion.div
                      key={dim.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-300 capitalize">{dim.name}</span>
                        <span className="text-white font-semibold">{dim.score}/100</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${dim.score}%` }}
                          transition={{ duration: 0.8, ease: 'easeOut' }}
                          className="h-full bg-gradient-to-r from-cyan-400 to-blue-400"
                        />
                      </div>
                      {dim.details && (
                        <div className="text-xs text-slate-400 mt-1">{dim.details}</div>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Alerts */}
                {healthScore.alerts && healthScore.alerts.length > 0 && (
                  <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <div className="flex items-center gap-2 text-red-400 text-sm font-semibold mb-2">
                      <AlertCircle size={16} />
                      Alertes
                    </div>
                    <ul className="space-y-1">
                      {healthScore.alerts.map((alert, i) => (
                        <li key={i} className="text-xs text-slate-300">{alert}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            </div>
          )}

          {/* Daily Insights */}
          <div className="col-span-8">
            <h2 className="text-white text-2xl font-semibold mb-6">Insights du Jour</h2>
            
            {insights.length === 0 ? (
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-12 text-center">
                <Clock className="mx-auto mb-4 text-slate-600" size={48} />
                <p className="text-slate-400">Aucun insight disponible aujourd'hui</p>
                <p className="text-slate-500 text-sm mt-2">Les insights sont générés automatiquement chaque jour</p>
              </div>
            ) : (
              <div className="space-y-4">
                {insights.map((insight, index) => (
                  <motion.div
                    key={insight.insight_id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6"
                    data-testid={`insight-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        {getCategoryIcon(insight.category)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-semibold text-lg">{insight.title}</h3>
                          <span className={`px-2 py-1 rounded-md text-xs border ${getPriorityBadge(insight.priority)}`}>
                            {insight.priority}
                          </span>
                          {insight.action_required && (
                            <span className="px-2 py-1 rounded-md text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30">
                              Action requise
                            </span>
                          )}
                        </div>
                        <p className="text-slate-300 text-sm mb-3">{insight.description}</p>
                        
                        {insight.metric_change && (
                          <div className="text-xs text-cyan-400 mb-2">📊 {insight.metric_change}</div>
                        )}
                        
                        {insight.recommendation && (
                          <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                            <div className="text-xs text-cyan-400 font-semibold mb-1">💡 Recommandation</div>
                            <div className="text-xs text-slate-300">{insight.recommendation}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthDashboard;
